import React from 'react';
import { Button } from '@repo/design-system';
import { space } from '@repo/design-tokens';
import styled from 'styled-components';

import {
  AssetCsvTemplateDownloadError,
  useAssetCsvTemplateDownload,
} from './useAssetCsvTemplateDownload';

/**
 * *Download the template* on a register that already has rows.
 *
 * The fetch, the token call, the bounded deadline, the `downloading` state and the error region
 * live in `useAssetCsvTemplateDownload`, which the empty register's drop zone uses too
 * (`equipment-page-start`, R10) — one implementation, two buttons. This component is the button,
 * its optional neighbour, and the error region laid out beneath them.
 *
 * ## Design-system notes
 *
 * `Button` and `Banner` are `@repo/design-system` primitives. `ghost`, not `frosted`: the host is
 * an opaque panel, and `frosted` is the glass variant whose translucent gradient has nothing
 * behind it here.
 */

interface Props {
  className?: string;
  /**
   * Anything the host wants BESIDE the button, on the button's own row (the banner stays
   * full-width beneath them).
   *
   * ⚠ No host passes it since `equipment-page-start` deleted *See the full column list*, its only
   * caller. Kept for now because that spec froze `AssetCsvTemplateButton.test.tsx` (which pins it)
   * as the proof the download hook moved without a behaviour change; the next feature to touch
   * this button removes it.
   */
  trailing?: React.ReactNode;
  /** `adopt`: the adoption template for `twinId` (`equipment-page-adoption`, R12). Default customer. */
  mode?: 'customer' | 'adopt';
  twinId?: number;
}

const AssetCsvTemplateButton: React.FC<Props> = ({ className, trailing, mode, twinId }) => {
  const { download, downloading, error } = useAssetCsvTemplateDownload();
  const adoption = mode === 'adopt';

  return (
    <div className={className}>
      <div className="row">
        {/* `loading`, NOT `disabled`. Disabling the control the operator just activated moves
            focus to <body> in Chrome and Firefox — so a keyboard user is thrown off the button,
            is not on it when its name changes to "Preparing…", and is not returned to it when it
            re-enables. `loading` keeps it focusable and in the tab order, sets aria-busy, and
            blocks activation through Button's own guarded handler, so nothing about
            double-click safety is lost. (Frontend/a11y reviewer.) */}
        <Button
          variant="ghost"
          onClick={() => void download(adoption ? { mode: 'adopt', twinId } : undefined)}
          loading={downloading}
        >
          {downloading
            ? 'Preparing…'
            : adoption
              ? 'Download the adoption template'
              : 'Download the template'}
        </Button>
        {trailing}
      </div>
      {/* The hook's always-mounted polite region; its banner sits below the row, full width. */}
      <AssetCsvTemplateDownloadError error={error} />
    </div>
  );
};

export default styled(AssetCsvTemplateButton)`
  .row {
    display: flex;
    align-items: center;
    gap: ${space[3]};
    flex-wrap: wrap;
  }

  /* Full width, BELOW the row — never inside it. In the row, the banner's height pushed the
     host's trailing control onto a second line and separated a pair that reads as one. */
  > [role='status']:not(:empty) {
    margin-top: ${space[3]};
  }
`;
